import React, { useState } from 'react';
import { Box, Typography, Paper, Divider, Button, Snackbar, Alert, useTheme } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ILogPremium } from '../types';
import BadgeSeveridade from '../../ui/BadgeSeveridade';
import { formatarDataHora } from '../utils';

interface PainelDetalheLogProps {
  log: ILogPremium | null;
}

export const PainelDetalheLog: React.FC<PainelDetalheLogProps> = ({ log }) => {
  const theme = useTheme();
  const [snackbarAberto, setSnackbarAberto] = useState(false);

  if (!log) {
    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '250px',
          color: theme.palette.text.secondary,
          textAlign: 'center'
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: '2.5rem', mb: 1.5, opacity: 0.5 }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Selecione um log para ver os detalhes completos da execução
        </Typography>
      </Paper>
    );
  }

  const copiarLogParaAreaTransferencia = () => {
    let textoCopia = `[${log.severidade.toUpperCase()}] ${log.titulo}\n`;
    textoCopia += `Data: ${formatarDataHora(log.dataHora)}\n`;
    textoCopia += `Origem: ${log.origem}\n`;
    textoCopia += `Mensagem: ${log.mensagem}\n`;
    if (log.detalhes) {
      textoCopia += `Detalhes Técnicos: ${JSON.stringify(log.detalhes, null, 2)}\n`;
    }

    navigator.clipboard.writeText(textoCopia)
      .then(() => {
        setSnackbarAberto(true);
      })
      .catch((err) => {
        console.error("Erro ao copiar log", err);
      });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        p: 3,
        color: theme.palette.text.primary,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        position: 'sticky',
        top: '20px'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem', fontWeight: 600 }}>
            Origem: {log.origem}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, mb: 1, color: theme.palette.text.primary }}>
            {log.titulo}
          </Typography>
        </Box>
        <BadgeSeveridade severidade={log.severidade} />
      </Box>

      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
        Registrado em: {formatarDataHora(log.dataHora)}
      </Typography>

      <Divider sx={{ borderColor: theme.palette.divider }} />

      <Box>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1, fontWeight: 600 }}>
          Mensagem
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(0, 0, 0, 0.03)',
            p: 1.5,
            borderRadius: '6px',
            border: `1px solid ${theme.palette.divider}`,
            maxHeight: '180px',
            overflowY: 'auto'
          }}
        >
          {log.mensagem}
        </Typography>
      </Box>

      {log.detalhes && (
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1, fontWeight: 600 }}>
            Detalhes Técnicos
          </Typography>
          <Box
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.8)' : 'rgba(0, 0, 0, 0.03)',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '6px',
              p: 1.5,
              maxHeight: '220px',
              overflowY: 'auto'
            }}
          >
            <Typography
              variant="caption"
              component="pre"
              sx={{
                fontFamily: 'monospace',
                color: theme.palette.mode === 'dark' ? '#38BDF8' : theme.palette.primary.main,
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all'
              }}
            >
              {JSON.stringify(log.detalhes, null, 2)}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ borderColor: theme.palette.divider, mt: 'auto' }} />

      <Button
        variant="contained"
        startIcon={<ContentCopyIcon />}
        onClick={copiarLogParaAreaTransferencia}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.1)' : 'rgba(42, 81, 247, 0.08)',
          color: theme.palette.mode === 'dark' ? '#38BDF8' : theme.palette.primary.main,
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.2)' : 'rgba(42, 81, 247, 0.15)'}`,
          boxShadow: 'none',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.2)' : 'rgba(42, 81, 247, 0.15)',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.3)' : 'rgba(42, 81, 247, 0.3)'
          }
        }}
      >
        Copiar Log Completo
      </Button>

      {/* Toast de sucesso */}
      <Snackbar
        open={snackbarAberto}
        autoHideDuration={3000}
        onClose={() => setSnackbarAberto(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarAberto(false)}
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: theme.palette.success.main,
            color: '#FFFFFF',
            fontWeight: 600,
            borderRadius: '8px'
          }}
        >
          Log copiado para a área de transferência!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PainelDetalheLog;
